import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Plus } from "lucide-react";
import { Stars } from "@/components/Stars";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { EASE } from "@/components/Reveal";

export function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const wished = has(product.slug);

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, ease: EASE, delay: (index % 4) * 0.08 }}
      className="group relative"
      data-testid={`product-card-${product.slug}`}
    >
      <div className="relative overflow-hidden rounded-2xl bg-beige aspect-[4/5]">
        <Link to={`/product/${product.slug}`} aria-label={product.name}>
          <img
            src={product.images ? product.images[0] : product.image}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
          />
        </Link>

        {product.badge && (
          <span
            className="absolute left-3 top-3 rounded-full bg-ivory/90 px-3 py-1 text-[0.6rem] font-medium uppercase tracking-[0.22em] text-ink backdrop-blur"
            data-testid={`product-badge-${product.slug}`}
          >
            {product.badge}
          </span>
        )}

        <button
          onClick={() => toggle(product)}
          aria-label="Toggle wishlist"
          data-testid={`wishlist-toggle-${product.slug}`}
          className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-ivory/85 text-ink backdrop-blur transition-transform duration-300 hover:scale-110"
        >
          <Heart size={16} strokeWidth={1.5} className={wished ? "fill-gold text-gold" : ""} />
        </button>

        {/* Quick add */}
        <div className="absolute inset-x-3 bottom-3 translate-y-4 opacity-0 transition-all duration-500 ease-out group-hover:translate-y-0 group-hover:opacity-100">
          <button
            onClick={() => addItem(product)}
            data-testid={`quick-add-${product.slug}`}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-ink py-3 text-[0.7rem] font-medium uppercase tracking-[0.22em] text-ivory transition-colors duration-300 hover:bg-gold"
          >
            <Plus size={14} strokeWidth={2} /> Quick Add
          </button>
        </div>
      </div>

      <div className="mt-4 flex items-start justify-between gap-3">
        <div>
          <Link to={`/product/${product.slug}`}>
            <h3 className="font-serif text-xl leading-tight text-ink">{product.name}</h3>
          </Link>
          <div className="mt-1.5 flex items-center gap-2">
            <Stars rating={product.rating} />
            <span className="text-xs text-muted-foreground">({product.review_count})</span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm text-ink">${product.price?.toFixed(0)}</p>
          {product.compare_at_price && (
            <p className="text-xs text-muted-foreground line-through">
              ${product.compare_at_price.toFixed(0)}
            </p>
          )}
        </div>
      </div>
    </motion.article>
  );
}
